import { type ResponseMESF } from "@dexteel/mesf-core";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import type { RowClassParams, RowStyle } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useState } from "react";
import { CenteredLazyLoading } from "../../../../../../controls/LazyLoading";
import { AgGridFooterActions } from "../../../../../../shared/data-entries/AgGridFooterActions";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import { useDataEntryContext } from "../../../context/reducer.provider";
import { useTimerContext } from "../../../context/timer.provider";
import { useLMFDataEntryActions } from "../../../hooks/useLMFDataEntryActions";
import { readAllPlugsFromPLC } from "../../../repositories/lmf.repositories";
import {
  hasTimeWithoutVolume,
  PlugMetricRow,
  usePlugMetricsTableData,
  PlugType,
} from "./hooks/usePlugMetricsTableData";
import { PlugMetricModal } from "../../modals/plug-metric/PlugMetricModal";
import { lmfDETablesStyles } from "../styles/lmfDETablesStyles";
import { useTheme } from "@mui/material/styles";
import { ProcessDataCheckboxFieldDE } from "../../../../../../shared/data-entries/process-data/ProcessDataCheckboxField";
import DurationInput from "@/shared/data-entries/common/DurationInput";
import { mmSsToDecimalMinutes } from "@/utils/durationHelpers";
import { Process } from "../../../models/lmf.models";
import { validateDurationAgainstProcess } from "../../../helpers/de-helper";

export const TablePlugMetrics = () => {
  const theme = useTheme();
  const tableStyles = lmfDETablesStyles(theme);
  const { pause, resume } = useTimerContext();
  const {
    state: { deProcess, isDataLoading },
    actions: { setProcess, setNotificationMessage, setErrorMessage },
  } = useDataEntryContext();
  const { refreshData, saveProcessData } = useLMFDataEntryActions();

  const process = deProcess?.process;

  const [showModal, setShowModal] = useState<"" | "EDIT">("");
  const [editPlugType, setEditPlugType] = useState<PlugType>("Inside Plug");

  const handleEdit = (plugType: PlugType) => {
    pause();
    setEditPlugType(plugType);
    setShowModal("EDIT");
  };

  const handleCloseModal = () => {
    resume();
    setShowModal("");
  };

  const readPlugsFromPLC = async (value?: string) => {
    if (value === "OK") {
      const resp = await readAllPlugsFromPLC({
        ProcessId: deProcess!.process!.ProcessId,
      });
      if (resp.ok) {
        setNotificationMessage("Read plug metrics from PLC successful");
        await refreshData();
        return resp as ResponseMESF;
      } else {
        setErrorMessage(resp.message);
        return {
          ok: false,
          message: "Read plug metrics from PLC failed",
        } as ResponseMESF;
      }
    } else {
      return {} as ResponseMESF;
    }
  };

  const { columns } = usePlugMetricsTableData({ onEdit: handleEdit });

  const getRowStyle = (
    params: RowClassParams<PlugMetricRow>,
  ): RowStyle | undefined => {
    if (hasTimeWithoutVolume(params.data)) {
      return { backgroundColor: "#FFF9C4" };
    }
    return undefined;
  };

  const rowData: PlugMetricRow[] = !process
    ? []
    : [
        {
          type: "Inside Plug",
          Vol: process.PlugInsideVol,
          Time: process.PlugInsideTime,
          TotalTime: process.PlugTotalInsideTime,
          Heats: process.PlugTotalHeats,
          isManual:
            process.IsPlugInsideTimeManual || process.IsPlugInsideVolManual,
        },
        {
          type: "Outside Plug",
          Vol: process.PlugOutsideVol,
          Time: process.PlugOutsideTime,
          TotalTime: process.PlugTotalOutsideTime,
          Heats: process.PlugTotalHeats,
          isManual:
            process.IsPlugOutsideTimeManual || process.IsPlugOutsideVolManual,
        },
      ];

  return (
    <Paper>
      <Box padding={1} sx={tableStyles.plugMetricsGrid}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columns}
          defaultColDef={{ ...defaultColDef, sortable: false }}
          getRowStyle={getRowStyle}
          loadingOverlayComponent={CenteredLazyLoading}
          {...defaultGridOptions}
        />
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <ProcessDataCheckboxFieldDE<Process>
                  field={"EmergencyLanceUsed"}
                  label="Emerg. Lance"
                  value={process?.EmergencyLanceUsed}
                  onChange={(value) => {
                    saveProcessData({
                      processId: process?.ProcessId!,
                      field: "EmergencyLanceUsed",
                      value: value,
                    });
                  }}
                  disabled={isDataLoading}
                />
              </Grid>
              <Grid item>
                <DurationInput
                  disabled={!process?.EmergencyLanceUsed}
                  label="Lance Time"
                  mode="mm:ss"
                  initMinutes={
                    process?.EmergencyLanceTime != null
                      ? process.EmergencyLanceTime / 60
                      : 0
                  }
                  onTimeChange={(time) => {
                    const totalSeconds = Math.round(
                      mmSsToDecimalMinutes(time) * 60,
                    );
                    const validation = validateDurationAgainstProcess(
                      totalSeconds,
                      process,
                      "Emergency Lance Time",
                    );
                    if (validation !== true) {
                      setErrorMessage(validation);
                      resume();
                      return;
                    }
                    setProcess({
                      ...process,
                      EmergencyLanceTime: totalSeconds,
                    });
                    saveProcessData({
                      processId: process?.ProcessId ?? 0,
                      field: "EmergencyLanceTime",
                      value: totalSeconds,
                    }).then(resume);
                  }}
                  onFocusHandler={() => pause()}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <AgGridFooterActions
              show={{ new: false, plc: true }}
              readFromPLC={readPlugsFromPLC}
              table="plugMetrics"
              pause={pause}
              resume={resume}
            />
          </Grid>
        </Grid>
      </Box>
      <PlugMetricModal
        showModal={showModal}
        onHide={handleCloseModal}
        process={process}
        plugType={editPlugType}
      />
    </Paper>
  );
};
